import React, { useMemo, useState } from "react";
import API from "../../utils/API";
import { FEATURES } from "../../config/features";
import { useBookData } from "../../data/BookDataProvider";
import { Button, Combobox, Field, Modal, useToast } from "../../ui";

/**
 * Staff-only "Link to household" (POST /api/prospects/:id/link-client {clientId}).
 * The migrated-prospect SYNC: many imported prospects were already converted to
 * clients; this attaches the prospect to that existing client. Server-side the
 * stage advances to bound and a LOST prospect is un-lost (its lostReason is
 * cleared, preserved in the Activity). Deliberately component-level and
 * self-contained — the next-phase Data Custodian data-fix queue reuses it.
 * Gated behind FEATURES.linkClient; the backend is the real boundary (writing
 * tier only).
 */
function LinkClientAction({ prospect, onLinked, variant = "ghost" }) {
  const toast = useToast();
  const { households } = useBookData();
  const [open, setOpen] = useState(false);
  const [clientId, setClientId] = useState(null);
  const [saving, setSaving] = useState(false);

  const options = useMemo(
    () =>
      (households || []).map((h) => ({
        value: h.id,
        label: h.name,
        description: [h.client && h.client.email, h.client && h.client.telephone]
          .filter(Boolean)
          .join(" · ")
      })),
    [households]
  );

  // Already tied to a client (e.g. a retention case) → nothing to link.
  const alreadyLinked = prospect && (prospect.clientRef || prospect.convertedToClientID);
  if (!FEATURES.linkClient || !prospect || alreadyLinked) {
    return null;
  }

  const confirm = async () => {
    if (!clientId) {
      toast.error("Pick the household to link.");
      return;
    }
    setSaving(true);
    try {
      await API.linkProspectToClient(prospect._id, clientId);
      toast.success("Linked to the existing household.");
      setOpen(false);
      setClientId(null);
      onLinked();
    } catch (err) {
      toast.error("Couldn't link — try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant={variant}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        Link to household
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={`Link ${prospect.name || "this prospect"} to a household`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" loading={saving} onClick={confirm}>
              Link
            </Button>
          </>
        }
      >
        <p className="feat-cell-secondary" style={{ marginTop: 0 }}>
          This attaches the prospect to an existing client — it becomes bound, and a lost prospect
          is brought back.
        </p>
        <Field label="Household" required htmlFor={`link-${prospect._id}`}>
          <Combobox
            options={options}
            value={clientId}
            onChange={setClientId}
            placeholder="Search the book…"
            emptyMessage="No household matches that."
          />
        </Field>
      </Modal>
    </>
  );
}

export default LinkClientAction;
